import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { disableBodyScrolling } from '@/utils/disableBodyScrolling';

@Component
export class SideCart extends ThemeComponent {
    @Prop({default: window.scrollY}) scrollPosition!: number;

    @Ref() sidecartClose!: HTMLDivElement;

    private previouslyFocusedElement: HTMLElement | null = null;

    init() {
        // Give initial value for scrollPosition
        this.scrollPosition = window.scrollY;

        this.eventListeners();
    }

    eventListeners() {
        this.openCart();
        this.closeCart();
        this.closeEvent();
        this.trapFocus();
        this.listenForEscapeKey();
    }

    openCart() {
        document.addEventListener('cartOpen', () => {

            //Update Cart
            document.dispatchEvent(new Event('cartChanged'));

            // Add active class
            this.classList.add('side-cart--active');
            const elements = document.querySelectorAll('.product-card-quick-add');
            for (const element of elements) {
                element.classList.remove('is-active');
            }
            // disable body scrolling
            this.scrollPosition = disableBodyScrolling(true, this.scrollPosition);

            // store previously focused element and move focus inside cart
            this.previouslyFocusedElement = document.activeElement as HTMLElement;
            const focusable = this.getFocusableElements();
            if (focusable.length > 0) {
                (focusable[0] as HTMLElement).focus();
            }
        });
    }

    closeCart() {
        document.addEventListener('cartClosed', () => {

            // Remove active class
            this.classList.remove('side-cart--active');

            // Enable body scrolling
            this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);

            // restore focus to previously focused element if possible
            if (this.previouslyFocusedElement) {
                this.previouslyFocusedElement.focus();
                this.previouslyFocusedElement = null;
            }
        });
    }

    closeEvent() {
        this.$listen(this.sidecartClose, 'click', (event) => {
            document.dispatchEvent(new Event('cartClosed'));
        });

        this.$listen(this, 'click', (event) => {
            if (event.target) {
                const clikedElement = event.target as HTMLElement;
                if (clikedElement.tagName === 'SIDE-CART') {
                    document.dispatchEvent(new Event('cartClosed'));
                }
            }
        });
    }

    listenForEscapeKey() {
        this.$listen(window, 'keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (this.classList.contains('side-cart--active')) {
                    document.dispatchEvent(new Event('cartClosed'));
                }
            }
        });
    }

    trapFocus() {
        this.$listen(this, 'keydown', (event: KeyboardEvent) => {
            if (event.key !== 'Tab') {
                return;
            }
            if (!this.classList.contains('side-cart--active')) {
                return;
            }
            const focusable = this.getFocusableElements();
            if (focusable.length === 0) {
                return;
            }
            const first = focusable[0] as HTMLElement;
            const last = focusable[focusable.length - 1] as HTMLElement;
            const active = document.activeElement as HTMLElement | null;

            if (event.shiftKey) {
                if (active === first || !active) {
                    event.preventDefault();
                    last.focus();
                }
            } else {
                if (active === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        });
    }

    private getFocusableElements(): HTMLElement[] {
        const selectors = [
            'a[href]',
            'area[href]',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            'iframe',
            'object',
            'embed',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable="true"]'
        ].join(',');

        const nodes = Array.from(this.querySelectorAll(selectors)) as HTMLElement[];
        // Filter out elements that are not visible
        return nodes.filter((el) => {
            const style = window.getComputedStyle(el);
            const rect = el.getBoundingClientRect();
            return style.visibility !== 'hidden' && style.display !== 'none' && rect.width > 0 && rect.height > 0;
        });
    }
}
